"use client";

import React from "react";
import { LifeBuoy, Shield, Mail, Phone, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqs = [{
  question: "How do I verify my trading account?",
  answer: "To comply with global financial regulations, you must upload a valid government-issued ID and proof of residence through our secure verification portal in your account settings."
}, {
  question: "What are the deposit and withdrawal methods?",
  answer: "We support instant bank transfers, major credit/debit cards, and selected e-wallets. All transactions are encrypted and processed through Tier-1 banking partners."
}, {
  question: "How does leverage work on TradeSphere?",
  answer: "Leverage allows you to gain larger market exposure with a smaller initial margin. Please be aware that while leverage can amplify profits, it also increases the risk of loss."
}, {
  question: "Is my capital secure with TradeSphere?",
  answer: "Yes, client funds are held in segregated bank accounts at top-tier financial institutions, ensuring your capital remains separate from our corporate operational funds."
}, {
  question: "When can I withdraw my trading profits?",
  answer: "You can request a withdrawal at any time. Funds are typically processed within 24 hours, provided you have sufficient free margin to cover any open market positions."
}];
export default function MarketplaceSupport() {
  return <section data-section-id="123472" id="common-questions" className="w-full py-16 md:py-24 bg-background text-foreground dark">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div className="text-center mb-10 lg:hidden">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-foreground">
            Trader Support
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Find technical answers or contact our institutional desk for personalized assistance.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative">
          {}
          <div className="lg:col-span-3 lg:sticky lg:top-24 order-2 lg:order-1">
            <Card className="border-border shadow-md bg-card h-full rounded-sm">
              <CardHeader>
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4 text-primary shadow-[0_0_10px_rgba(6,182,212,0.2)]">
                  <LifeBuoy className="w-6 h-6" />
                </div>
                <CardTitle className="text-xl font-bold tracking-tight">Institutional Desk</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Our professional support team is available 24/7 for account queries.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="font-medium text-foreground">desk@tradesphere.com</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="font-medium text-foreground">+1 (800) 555-TRADE</span>
                </div>
              </CardContent>
              <CardFooter>
                <Button asChild className="w-full rounded-sm border-primary text-primary hover:bg-primary/5" variant="outline">
                  <Link to="#">Open Ticket</Link>
                </Button>
              </CardFooter>
            </Card>
          </div>

          {}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="hidden lg:block text-center mb-10">
              <h2 className="text-4xl font-black tracking-tighter sm:text-5xl mb-4 text-foreground uppercase">
                Common Questions
              </h2>
              <p className="text-muted-foreground font-medium">
                Essential information for navigating the TradeSphere ecosystem.
              </p>
            </div>
            <div className="bg-card rounded-sm border border-border p-2 sm:p-6 shadow-xl shadow-primary/5">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border px-2 sm:px-4">
                    <AccordionTrigger className="text-left font-bold text-lg hover:no-underline hover:text-primary transition-colors py-5 tracking-tight">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5 font-medium">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          {}
          <div className="lg:col-span-3 lg:sticky lg:top-24 order-3">
            <Card className="border-border shadow-md bg-card h-full rounded-sm">
              <CardHeader>
                <div className="w-12 h-12 rounded-sm bg-chart-2/10 flex items-center justify-center mb-4 text-chart-2">
                  <Shield className="w-6 h-6" />
                </div>
                <CardTitle className="text-xl font-bold tracking-tight">Security & Compliance</CardTitle>
                <CardDescription className="text-muted-foreground">
                  How we maintain institutional-grade security for your assets.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                  We utilize multi-sig cold storage, end-to-end encryption, and rigorous KYC/AML procedures to ensure a compliant and secure trading environment.
                </p>
              </CardContent>
              <CardFooter>
                <Button asChild className="w-full group rounded-sm" variant="ghost">
                  <Link to="/FundingPage">
                    Funding Policy
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}